import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Box,
  Pagination,
  ImageList,
  ImageListItem,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment,
  Backdrop,
  CircularProgress,
  Fade,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { fetchApi, uploadBase64Image } from '../services/api';
import { SmartEditorRef } from '../smarteditor/component/sEditor';

interface LocationData {
  코드명: string;
  국가번호: number;
  국가한글명: string;
  도시번호: number;
  도시한글명: string;
}

interface PlaceData {
  장소번호: number;
  장소한글명: string;
}

interface ImageItem {
  순서: number;
  구분: string;
  경로: string;
  이미지번호: number;
  원본이미지경로: string;
  노출이미지경로: string;
  파일명: string;
  업체: string;
  장소명: string;
  속성명: string;
  업로드: string;
  업로드자: string;
}

interface ImageGalleryDialogProps {
  open: boolean;
  onClose: () => void;
  onImageSelect: (imageUrl: string) => void;
  editorRef?: React.RefObject<SmartEditorRef>;
}

const ImageGalleryDialog: React.FC<ImageGalleryDialogProps> = ({
  open,
  onClose,
  onImageSelect,
  editorRef,
}) => {
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [places, setPlaces] = useState<PlaceData[]>([]);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [selectedCity, setSelectedCity] = useState<number | null>(null);
  const [currentPlaceId, setCurrentPlaceId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [currentSearchType, setCurrentSearchType] = useState<'국가' | '도시' | '장소'>('장소');
  const [loading, setLoading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadType, setUploadType] = useState<'City' | 'Place'>('City');

  useEffect(() => {
    if (open) {
      fetchLocations();
    }
  }, [open]);

  const fetchLocations = async () => {
    try {
      const response = await fetchApi<{ table: LocationData[] }>('ITN_S_도시국가리스트', []);
      if (response.data?.table) {
        setLocations(response.data.table);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      setLocations([]);
    }
  };

  // const fetchPlaces = async (cityId: number) => {
  //   try {
  //     const response = await fetchApi<{ table: PlaceData[] }>('ITN_S_장소리스트', [
  //       { "컬럼명": "@도시번호", "값": cityId }
  //     ]);
  //     if (response.data?.table) {
  //       setPlaces(response.data.table);
  //     } else {
  //       setPlaces([]);
  //     }
  //   } catch (error) {
  //     console.error('Failed to fetch places:', error);
  //     setPlaces([]);
  //   }
  // };

  const fetchImages = async (id: number, type: '국가' | '도시' | '장소', pageNum: number = 1, append: boolean = false) => {
    setCurrentSearchType(type);
    try {
      const response = await fetchApi<any>('RCT_S_이미지장소경로리스트_메뉴이미지', [
        { "컬럼명": "@공급업체번호", "값": "1" },
        { "컬럼명": "@구분", "값": type },
        { "컬럼명": "@키번호", "값": id },
        { "컬럼명": "@페이지번호", "값": pageNum }
      ]);
      
      if (response.data?.table1) {
        if (append) {
          setImages(prev => [...prev, ...response.data.table1]);
        } else {
          setImages(response.data.table1);
        }
        setTotalPages(response.data.table[0]?.총페이지수 || 1);
        setPage(pageNum);
        setCurrentPlaceId(id);
      } else {
        if (!append) {
          setImages([]);
          setTotalPages(1);
          setPage(1);
        }
      }
    } catch (error) {
      console.error('Failed to fetch images:', error);
      if (!append) {
        setImages([]);
        setTotalPages(1);
        setPage(1);
      }
    }
  };

  const handlePlaceSelect = async (placeId: number) => {
    setCurrentPlaceId(placeId);
    try {
      const response = await fetchApi<any>('RCT_S_이미지장소경로리스트_메뉴이미지', [
        { "컬럼명": "@공급업체번호", "값": "1" },
        { "컬럼명": "@구분", "값": "장소" },
        { "컬럼명": "@키번호", "값": placeId },
        { "컬럼명": "@페이지번호", "값": 1 }
      ]);
      
      if (response.data?.table1) {
        setImages(response.data.table1);
        setTotalPages(response.data.table[0]?.총페이지수 || 1);
        setPage(1);
      } else {
        setImages([]);
        setTotalPages(1);
        setPage(1);
      }
    } catch (error) {
      console.error('Failed to fetch images:', error);
      setImages([]);
      setTotalPages(1);
      setPage(1);
    }
  };

  const handleCountrySelect = (countryId: number) => {
    setSelectedCountry(countryId);
    setSelectedCity(null);
    setPlaces([]);
    setCurrentPlaceId(null);
    fetchImages(countryId, '국가', 1);
  };

  const handleCodeSelect = (code: string) => {
    setSelectedCode(code);
    setSelectedCountry(null);
    setSelectedCity(null);
    setPlaces([]);
    setCurrentPlaceId(null);
  };

  const handleCitySelect = async (cityId: number) => {
    setSelectedCity(cityId);
    try {
      const postData = [
        { "컬럼명": "@도시번호", "값": cityId.toString() }
      ];

      const [placeResponse, imageResponse] = await Promise.all([
        fetchApi<any>('ITN_S_장소리스트', postData),
        fetchApi<any>('RCT_S_이미지장소경로리스트_메뉴이미지', [
          { "컬럼명": "@공급업체번호", "값": "1" },
          { "컬럼명": "@구분", "값": "도시" },
          { "컬럼명": "@키번호", "값": cityId },
          { "컬럼명": "@페이지번호", "값": 1 }
        ])
      ]);

      if (placeResponse.data?.table) {
        setPlaces(placeResponse.data.table);
      }

      if (imageResponse.data?.table1) {
        setImages(imageResponse.data.table1);
        setTotalPages(imageResponse.data.table[0]?.총페이지수 || 1);
        setPage(1);
      } else {
        setImages([]);
        setTotalPages(1);
        setPage(1);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setPlaces([]);
      setImages([]);
      setTotalPages(1);
      setPage(1);
    }
  };

  const handleImageSelect = (imageUrl: string) => {
    if (editorRef?.current) {
      const imgHtml = `<img src="${imageUrl}" style="width:245px; height:150px; margin:4px; object-fit:cover;" />`;
      editorRef.current.pasteHtmlAtCursor(imgHtml);
    }
    onImageSelect(imageUrl);
    onClose();
  };

  const handleSearch = async () => {
    if (searchText.trim()) {
      searchImages();
    }
  };

  const searchImages = async () => {
    try {
      setLoading(true);
      const response = await fetchApi<any>('RCT_S_이미지장소경로리스트_이미지', [
        { "컬럼명": "@공급업체번호", "값": "1" },
        { "컬럼명": "@구분", "값": "2" },
        { "컬럼명": "@변수명", "값": searchText },
        { "컬럼명": "@페이지번호", "값": 1 }
      ]);
      
      if (response.data?.table1) {
        setImages(response.data.table1);
        setTotalPages(response.data.table[0]?.총페이지수 || 1);
        setPage(1);
      } else {
        setImages([]);
        setTotalPages(1);
        setPage(1);
      }
    } catch (error) {
      console.error('Failed to search images:', error);
      setImages([]);
      setTotalPages(1);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    if (currentPlaceId) {
      fetchImages(currentPlaceId, currentSearchType, value);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'City' | 'Place') => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const filePath = type === 'City' ? 'City' : 'Place';
      const response = await uploadBase64Image(file, filePath);

      console.log(response.data.url, response.status);
      if (response.status === 200 && response.data?.url) {
        // 새 이미지 아이템 생성
        const newImageItem: ImageItem = {
          순서: 0,
          구분: type,
          경로: response.data.url,
          이미지번호: Date.now(), // 임시 번호
          원본이미지경로: response.data.url,
          노출이미지경로: response.data.url,
          파일명: file.name,
          업체: '',
          장소명: type === 'City' ? 
            locations.find(loc => loc.도시번호 === selectedCity)?.도시한글명 || '' :
            places.find(place => place.장소번호 === currentPlaceId)?.장소한글명 || '',
          속성명: '',
          업로드: new Date().toISOString(),
          업로드자: '',
        };

        console.log(newImageItem);
        // 이미지 리스트 맨 앞에 추가
        setImages(prev => [newImageItem, ...prev]);

        // 프로시저 호출하여 데이터베이스에 저장
        const companyNo = localStorage.getItem('CompanyNo');
        const userId = localStorage.getItem('UserNo');
        
        // 썸네일 경로 생성 함수
        const createThumbPath = (path: string) => {
          const parts = path.split('/');
          const fileName = parts.pop(); // 파일명 추출
          return [...parts, 'Thumb', fileName].join('/'); // Thumb 폴더 추가
        };

        const imagePath = response.data.url.replace('https://kr.object.iwinv.kr/orgin/', '');
        const thumbPath = createThumbPath(imagePath);
        
        if (type === 'City' && selectedCity) {
          await fetchApi('WSP_T_도시_이미지업로드', [
            { "컬럼명": "@업체번호", "값": companyNo },
            { "컬럼명": "@이미지경로", "값": imagePath },
            { "컬럼명": "@파일명", "값": response.data.url.split('/').pop() },
            { "컬럼명": "@처리자", "값": userId },
            { "컬럼명": "@도시번호", "값": selectedCity },
            { "컬럼명": "@썸네일파일경로", "값": thumbPath }
          ]);
        } else if (type === 'Place' && currentPlaceId) {
          await fetchApi('WSP_T_장소_이미지업로드', [
            { "컬럼명": "@업체번호", "값": companyNo },
            { "컬럼명": "@이미지경로", "값": imagePath },
            { "컬럼명": "@파일명", "값": response.data.url.split('/').pop() },
            { "컬럼명": "@처리자", "값": userId },
            { "컬럼명": "@장소번호", "값": currentPlaceId },
            { "컬럼명": "@썸네일파일경로", "값": thumbPath }
          ]);
        }
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = (type: 'City' | 'Place') => {
    setUploadType(type);
    fileInputRef.current?.click();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          width: '1600px',
          maxWidth: '1600px',
        },
      }}
    >
      <DialogTitle sx={{ 
        height: 70,
        minHeight: 70,
        bgcolor: '#1976d2',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        mb: 2
      }}>
        <Typography variant="h6">이미지 갤러리</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="검색어를 입력하세요"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            sx={{ 
              width: 300,
              mr: 2.5,
              backgroundColor: 'white',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                height: '32px',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon onClick={() => handleSearch()} sx={{ cursor: 'pointer' }} />
                </InputAdornment>
              ),
            }}
          />
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, position: 'relative' }}>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={(e) => handleFileUpload(e, uploadType)}
        />
        <Backdrop
          sx={{
            position: 'absolute',
            color: '#fff',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
          open={loading}
        >
          <Fade in={loading} timeout={800}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <CircularProgress color="inherit" size={60} />
              <Typography variant="h6" component="div" sx={{
                textAlign: 'center',
                animation: 'pulse 2s infinite'
              }}>
                이미지를 검색중입니다...
              </Typography>
            </Box>
          </Fade>
        </Backdrop>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: 13 }}>
              지역
            </Typography>
            <List sx={{ 
              bgcolor: 'background.paper',
              border: '1px solid #ddd',
              borderRadius: 1,
              height: 210,
              overflow: 'auto',
              padding: '5px',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
              },
            }}>
              <Grid container>
                {Array.from(new Set(locations.map(item => item.코드명))).map((code) => (
                  <Grid item xs={4} key={code}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={selectedCode === code}
                        onClick={() => handleCodeSelect(code)}
                        sx={{
                          fontSize: 12,
                          padding: '4px',
                          paddingLeft: '5px',
                          borderRadius: '4px',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                            },
                            '& .MuiListItemText-primary': {
                              color: 'white',
                            }
                          }
                        }}
                      >
                        <ListItemText primary={code} sx={{ 
                          '& .MuiListItemText-primary': { 
                            fontSize: 12,
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                          } 
                        }} />
                      </ListItemButton>
                    </ListItem>
                  </Grid>
                ))}
              </Grid>
            </List>
          </Grid>
          <Grid item xs={2.7}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: 13 }}>
              국가
            </Typography>
            <List sx={{ 
              bgcolor: 'background.paper',
              border: '1px solid #ddd',
              borderRadius: 1,
              height: 210,
              overflow: 'auto',
              padding: '5px',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
              },
            }}>
              <Grid container>
                {locations
                  .filter(item => item.코드명 === selectedCode)
                  .filter((item, index, self) => 
                    index === self.findIndex(t => t.국가한글명 === item.국가한글명)
                  )
                  .map((item) => (
                    <Grid item xs={4} key={item.국가한글명}>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={selectedCountry === item.국가번호}
                          onClick={() => handleCountrySelect(item.국가번호)}
                          sx={{
                            fontSize: 12,
                            padding: '4px',
                            paddingLeft: '5px',
                            borderRadius: '4px',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            '&.Mui-selected': {
                              bgcolor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              },
                              '& .MuiListItemText-primary': {
                                color: 'white',
                              }
                            }
                          }}
                        >
                          <ListItemText primary={item.국가한글명} sx={{ 
                            '& .MuiListItemText-primary': { 
                              fontSize: 12,
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap'
                            } 
                          }} />
                        </ListItemButton>
                      </ListItem>
                    </Grid>
                  ))}
              </Grid>
            </List>
          </Grid>
          <Grid item xs={2.7}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 13 }}>
                도시
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddPhotoAlternateIcon />}
                disabled={!selectedCity || !locations.some(loc => loc.도시번호 === selectedCity)}
                onClick={() => handleUploadClick('City')}
                sx={{ 
                  height: '24px',
                  fontSize: '12px',
                  textTransform: 'none',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                }}
              >
                이미지 추가
              </Button>
            </Box>
            <List sx={{ 
              bgcolor: 'background.paper',
              border: '1px solid #ddd',
              borderRadius: 1,
              height: 210,
              overflow: 'auto',
              padding: '5px',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
              },
            }}>
              <Grid container>
                {locations
                  .filter(item => 
                    item.코드명 === selectedCode && 
                    item.국가번호 === selectedCountry
                  )
                  .map((item) => (
                    <Grid item xs={4} key={item.도시번호}>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={selectedCity === item.도시번호}
                          onClick={() => handleCitySelect(item.도시번호)}
                          sx={{
                            fontSize: 12,
                            padding: '4px',
                            paddingLeft: '5px',
                            borderRadius: '4px',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            '&.Mui-selected': {
                              bgcolor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              },
                              '& .MuiListItemText-primary': {
                                color: 'white',
                              }
                            }
                          }}
                        >
                          <ListItemText primary={item.도시한글명} sx={{ 
                            '& .MuiListItemText-primary': { 
                              fontSize: 12,
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap'
                            } 
                          }} />
                        </ListItemButton>
                      </ListItem>
                    </Grid>
                  ))}
              </Grid>
            </List>
          </Grid>
          <Grid item xs={4.6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 13 }}>
                장소
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddPhotoAlternateIcon />}
                disabled={!currentPlaceId || !places.some(place => place.장소번호 === currentPlaceId)}
                onClick={() => handleUploadClick('Place')}
                sx={{ 
                  height: '24px',
                  fontSize: '12px',
                  textTransform: 'none',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                }}
              >
                이미지 추가
              </Button>
            </Box>
            <List sx={{ 
              bgcolor: 'background.paper',
              border: '1px solid #ddd',
              borderRadius: 1,
              height: 210,
              overflow: 'auto',
              padding: '5px',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
              },
            }}>
              <Grid container>
                {places?.map((place) => (
                  <Grid item xs={3} key={place.장소번호}>
                    <ListItemButton
                      onClick={() => handlePlaceSelect(place.장소번호)}
                      selected={currentPlaceId === place.장소번호}
                      sx={{
                        fontSize: 12,
                        padding: '4px',
                        paddingLeft: '5px',
                        borderRadius: '4px',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '& .MuiListItemText-primary': {
                            color: 'white',
                          }
                        }
                      }}
                    >
                      <ListItemText primary={place.장소한글명} sx={{ 
                        '& .MuiListItemText-primary': { 
                          fontSize: 12,
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap'
                        } 
                      }} />
                    </ListItemButton>
                  </Grid>
                ))}
              </Grid>
            </List>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <ImageList cols={5} gap={16} sx={{ overflow: 'hidden' }}>
            {images?.map((item) => (
              <ImageListItem 
                key={item.이미지번호}
                onClick={() => handleImageSelect(item.노출이미지경로)}
                sx={{ 
                  cursor: 'pointer',
                  position: 'relative',
                }}>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    padding: '4px',
                    fontSize: '12px',
                    textAlign: 'center',
                    zIndex: 1,
                  }}
                >
                  {item.장소명}
                </Box>
                <img
                  src={item.노출이미지경로}
                  alt={item.파일명}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </ImageListItem>
            ))}
          </ImageList>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ImageGalleryDialog;
